import React from 'react';
import { useTranslation } from 'react-i18next';

interface TrendDataPoint {
  composite_score: number;
  recorded_at: string;
}

interface MindsetTrendChartProps {
  data: TrendDataPoint[];
  height?: number;
  width?: number;
  className?: string;
}

const ROLLING_WINDOW_DAYS = 7;
const FALLBACK_RECORDS = 5;

/**
 * Compact arrow-based trend indicator for composite mindset score.
 * Uses a 7-day rolling window (falls back to last 5 records if < 2 in window).
 *
 * Improving:  ▲ / ▲▲ / ▲▲▲  +N%  (green)
 * Stable:     — Stable              (amber)
 * Declining:  "Trending for Xd"      (muted, no red arrows)
 */
export const MindsetTrendChart: React.FC<MindsetTrendChartProps> = ({
  data,
  className = '',
}) => {
  const { t } = useTranslation();

  if (!data || data.length < 2) return null;

  // Try 7-day rolling window first
  const now = new Date();
  const cutoff = new Date(now.getTime() - ROLLING_WINDOW_DAYS * 24 * 60 * 60 * 1000);
  const recentData = data.filter((d) => new Date(d.recorded_at) >= cutoff);

  // Use rolling window if 2+ records, otherwise fall back to last N records
  const usedRollingWindow = recentData.length >= 2;
  const windowData = usedRollingWindow
    ? recentData
    : data.slice(-FALLBACK_RECORDS);

  if (windowData.length < 2) return null;

  const windowLabel = usedRollingWindow ? '7d' : `${windowData.length} sessions`;

  const first = windowData[0].composite_score;
  const last = windowData[windowData.length - 1].composite_score;
  const diff = last - first;
  const absDiff = Math.abs(diff);
  const pct = first !== 0 ? (diff / first) * 100 : 0;

  // Determine strength: 1 arrow (mild), 2 arrows (moderate), 3 arrows (strong)
  let strength = 0;
  if (absDiff > 2) strength = 1;
  if (absDiff > 8) strength = 2;
  if (absDiff > 15) strength = 3;

  const isUp = diff > 0;
  const isStable = strength === 0;
  const isDeclining = !isUp && !isStable;

  // Improving: show green arrows + percentage
  if (isUp && !isStable) {
    const color = strength >= 3 ? 'text-emerald-600' : strength >= 2 ? 'text-emerald-500' : 'text-emerald-400';
    const label = strength >= 3 ? t('mindsetTrend.strongImprovement') : strength >= 2 ? t('mindsetTrend.improving') : t('mindsetTrend.slightImprovement');

    return (
      <span className={`inline-flex items-center gap-0.5 ${color} ${className}`} title={label}>
        <span className="text-xs leading-none">
          {Array.from({ length: strength }, (_, i) => (
            <span key={i}>▲</span>
          ))}
        </span>
        <span className="text-xs font-medium">+{Math.round(pct)}%</span>
        <span className="text-[10px] opacity-50 ml-0.5">{windowLabel}</span>
      </span>
    );
  }

  // Stable or declining: show "Trending for Xd" with no descriptive labels
  const firstDate = new Date(windowData[0].recorded_at);
  const trendDays = Math.max(1, Math.floor((now.getTime() - firstDate.getTime()) / (24 * 60 * 60 * 1000)));

  return (
    <span className={`inline-flex items-center gap-0.5 text-gray-400 ${className}`}>
      <span className="text-xs">
        {t('mindsetTrend.trendLabel', { days: trendDays })}
      </span>
    </span>
  );
};
